import { useEffect, useState } from "react";
import { JSX } from "react";
import { useAdCategoryStore } from "../store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import Dropdown from "@/components/ui/DropDown";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import { X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAdBuilderStore from "@/pages/ad-builder/store/ad-builder-store";

export default function CreateAdCategoriesStepFive(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    canvasBuilderData,
    selectedInterface,
    selectedAdSpecs,
    selectedAdInfoBlocks,
    fetchCanvasBuilderData,
    setSelectedInterface,
    setSelectedAdSpecs,
    setSelectedAdInfoBlocks,
    addAdSpec,
    removeAdSpec,
    addAdInfoBlock,
    removeAdInfoBlock,
  } = useAdCategoryStore(
    useShallow((state) => ({
      canvasBuilderData: state.canvasBuilderData,
      selectedInterface: state.selectedInterface,
      selectedAdSpecs: state.selectedAdSpecs,
      selectedAdInfoBlocks: state.selectedAdInfoBlocks,
      fetchCanvasBuilderData: state.fetchCanvasBuilderData,
      setSelectedInterface: state.setSelectedInterface,
      setSelectedAdSpecs: state.setSelectedAdSpecs,
      setSelectedAdInfoBlocks: state.setSelectedAdInfoBlocks,
      addAdSpec: state.addAdSpec,
      removeAdSpec: state.removeAdSpec,
      addAdInfoBlock: state.addAdInfoBlock,
      removeAdInfoBlock: state.removeAdInfoBlock,
    })),
  );

  const { loadCanvas } = useAdBuilderStore(
    useShallow((state) => ({
      loadCanvas: state.loadCanvas,
    })),
  );

  const { setError } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
    })),
  );

  useEffect(() => {
    const fetchData = async () => {
      const adInputId = searchParams.get("adInputId");
      if (!adInputId) return;
      try {
        setIsLoading(true);
        // Reset stale store values before loading fresh Step 5 data
        useAdCategoryStore.getState().resetValue();
        await fetchCanvasBuilderData(adInputId);

        // After canvas builder data is loaded, restore previously saved step 5 selections
        const mappingId = useAdCategoryStore.getState().canvasBuilderData?.ad_category_mapping_id;
        if (mappingId) {
          await loadCanvas(mappingId).catch(() => {
            // No canvas saved yet — that's fine
          });
          const canvasInfo = useAdBuilderStore.getState().loadedCanvasData?.canvas_master?.canvas_info_aggregated ?? {};
          if (canvasInfo.selectedInterface) setSelectedInterface(canvasInfo.selectedInterface);
          if (Array.isArray(canvasInfo.selectedAdSpecs)) setSelectedAdSpecs(canvasInfo.selectedAdSpecs);
          if (Array.isArray(canvasInfo.selectedAdInfoBlocks)) setSelectedAdInfoBlocks(canvasInfo.selectedAdInfoBlocks);
        }
      } catch (error: any) {
        setError(error?.message || "Failed to load canvas builder data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-screen h-screen">
        <Spinner size={30} />
      </div>
    );
  }

  if (!canvasBuilderData) {
    return (
      <div className="mt-6">
        <div className="text-center text-gray-500">
          No canvas builder data available
        </div>
      </div>
    );
  }

  const {
    ad_type,
    ad_category,
    publisher,
    interface_types,
    parameters,
    ad_info_blocks,
  } = canvasBuilderData;

  // Prepare interface options
  const interfaceOptions = (interface_types || []).map(
    (interfaceType: any) => ({
      title:
        interfaceType.name.charAt(0).toUpperCase() +
        interfaceType.name.slice(1),
      value: interfaceType.id,
    }),
  );

  // Prepare ad specs options
  const adSpecsOptions = (parameters || []).map((param: any) => ({
    title: param.parameter_definition.label,
    value: param.parameter_definition.key,
  }));

  // Prepare ad info blocks options - get keys from ad_info_blocks object
  const adInfoBlocksOptions = ad_info_blocks
    ? Object.keys(ad_info_blocks).map((key) => ({
        title: key.charAt(0).toUpperCase() + key.slice(1),
        value: key,
      }))
    : [];

  const handleAdSpecSelect = (value: string) => {
    if (value && !selectedAdSpecs.includes(value)) {
      addAdSpec(value);
    }
  };

  const handleAdInfoBlockSelect = (value: string) => {
    if (value && !selectedAdInfoBlocks.includes(value)) {
      addAdInfoBlock(value);
    }
  };

  const handleCreateTemplate = () => {
    const adCategoryMappingId = canvasBuilderData?.ad_category_mapping_id;
    const path = adCategoryMappingId
      ? `/ad-builder?adCategoryMappingId=${adCategoryMappingId}`
      : "/ad-builder";
    navigate(path);
  };

  return (
    <div className="mt-6">
      <div className="font-bold text-xl mb-2">Step 5: Create Ad Template</div>
      <div className="text-sm text-gray-600 mb-4">
        <span className="font-semibold">Publisher:</span>{" "}
        {publisher?.name || "User Defined"} <span className="mx-2">|</span>
        <span className="font-semibold">Ad Type:</span>{" "}
        {ad_type?.name || "Facebook Carousel"} <span className="mx-2">|</span>
        <span className="font-semibold">Ad Parameters:</span> #
      </div>
      <hr className="border-[#EA33C0] mb-6" />

      {/* Ad Template Type */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-3">Ad Template Type</h3>
        <div className="mb-2">
          <label className="text-sm text-gray-500 mb-1 block">Interface</label>
          <Dropdown
            options={interfaceOptions}
            selected={selectedInterface}
            setSelected={setSelectedInterface}
            placeholder="Select Option"
            className="w-64"
          />
        </div>
        {selectedInterface && (
          <div className="mt-2 text-xs text-gray-500">
            Selected:{" "}
            {interfaceOptions.find((opt) => opt.value === selectedInterface)
              ?.title || ""}
          </div>
        )}
      </div>

      {/* Ad Specs Overview */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-1">Ad Specs Overview</h3>
        <p className="text-sm text-gray-500 mb-3">
          Select ad specs from the dropdown menu to display below the Ad Builder
          Template.
        </p>
        <Dropdown
          options={adSpecsOptions}
          selected=""
          setSelected={handleAdSpecSelect}
          placeholder="Add Specs..."
          className="w-96"
        />
        {selectedAdSpecs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedAdSpecs.map((specKey) => {
              const spec = adSpecsOptions.find((opt) => opt.value === specKey);
              return (
                <div
                  key={specKey}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-sm"
                >
                  <span>{spec?.title || specKey}</span>
                  <button
                    onClick={() => removeAdSpec(specKey)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ad Info Blocks */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-1">Ad Info Blocks</h3>
        <p className="text-sm text-gray-500 mb-3">
          Select information blocks from the dropdown menu to display below the
          Ad Builder Template.
        </p>
        <Dropdown
          options={adInfoBlocksOptions}
          selected=""
          setSelected={handleAdInfoBlockSelect}
          placeholder="Add Blocks ..."
          className="w-96"
        />
        {selectedAdInfoBlocks.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedAdInfoBlocks.map((blockKey) => {
              const block = adInfoBlocksOptions.find(
                (opt) => opt.value === blockKey,
              );
              return (
                <div
                  key={blockKey}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-sm"
                >
                  <span>{block?.title || blockKey}</span>
                  <button
                    onClick={() => removeAdInfoBlock(blockKey)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Template Builder */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-4">Template Builder</h3>
        <button
          onClick={handleCreateTemplate}
          className="flex flex-col items-center justify-center w-40 h-40 border-2 border-pink-500 rounded-lg hover:bg-pink-50 transition-colors cursor-pointer"
        >
          <div className="text-pink-500 text-4xl mb-2">+</div>
          <div className="text-pink-500 text-sm font-medium">
            Create Template
          </div>
        </button>
      </div>
    </div>
  );
}
